import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import useCommunityPolicies from '@aurora/shared-client/components/community/useCommunityPolicies';
import { canAccessPageBuilder as checkCanAccessPageBuilder } from '@aurora/shared-client/helpers/nodes/NodePolicyHelper';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import Icons from '@aurora/shared-client/icons';
import { EndUserComponent, EndUserPages } from '@aurora/shared-types/pages/enums';
import React from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import endUserComponentRegistry from '../../../../features/endUserComponentRegistry';
import { usePageEditorLink } from '../../../usePageEditorLink';
import useTranslation from '../../../useTranslation';
import localStyles from '../../Category/CategoryActionMenu/CategoryActionMenu.module.pcss';

/**
 * Display Search Action Menu
 *
 * Is not displayed when user does not meet policy criteria to see the any of the menu items
 *
 * @author Kiran Kumari
 */
const SearchActionMenu: React.FC<React.PropsWithChildren> = () => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.SEARCH_ACTION_MENU
  );
  const { isEditable: isPageEditable } = endUserComponentRegistry.getPageDescriptor(
    EndUserPages.SearchResultsPage
  );
  const { link: pageEditorLink } = usePageEditorLink();

  const { data: communityPoliciesData, loading: communityPoliciesLoading } = useCommunityPolicies(
    module,
    {
      useCanAccessPageBuilder: true
    }
  );

  if (textLoading || communityPoliciesLoading) {
    return null;
  }

  const canAccessPageBuilder: boolean = checkCanAccessPageBuilder(communityPoliciesData?.coreNode);

  /**
   * Renders the edit page link in Category Action Menu
   */
  function renderEditPageLink(): React.ReactElement {
    return pageEditorLink(
      <Dropdown.Item key={'editPage'} data-testid={'SearchActionMenuItem.EditPage'}>
        {formatMessage('editPage')}
      </Dropdown.Item>
    );
  }

  return (
    isPageEditable &&
    canAccessPageBuilder && (
      <>
        <Dropdown
          className={cx('lia-g-ml-10')}
          title={formatMessage('actionMenu.title')}
          aria-label={formatMessage('actionMenu.ariaLabel')}
        >
          <Dropdown.Toggle
            id="lia-search-action-menu"
            as={Button}
            aria-label={formatMessage('toggleButtonLabel')}
            className={cx('lia-g-icon-btn')}
            variant={ButtonVariant.NO_VARIANT}
          >
            <Icon
              icon={Icons.GearIcon}
              size={IconSize.PX_20}
              color={IconColor.GRAY_900}
              testId="SearchActionMenuItem.GearIcon"
            />
          </Dropdown.Toggle>
          <Dropdown.Menu
            align="right"
            aria-labelledby="lia-search-action-menu"
            className={cx('lia-dropdown-menu')}
            popperConfig={dropdownPopperConfig}
            renderOnMount
            data-testid="SearchActionMenuItem.dropdown"
          >
            {renderEditPageLink()}
          </Dropdown.Menu>
        </Dropdown>
      </>
    )
  );
};

export default SearchActionMenu;
